import { Baseline } from '@shared/models/project.model';
import { getSpaceDemandInWhPerM2 } from './space-demand.helpers';
import { getWindowArea } from '@shared/templates/categories/windows-doors';
import { inTestMode } from '@shared/constants';
import { getUrlParam } from '../url.helpers';
import { isOneOf } from '../array.helpers';

/** Set to `true` to print energy debug tables in the app and in Vitest. */
const DEBUG_ENERGY = false;
const DEBUG_PARAMS = ['energy', 'all', 'true'];

const isEnergyDebugEnabled = (): boolean => {
  if (DEBUG_ENERGY) return true;
  if (inTestMode()) return false;
  return isOneOf(DEBUG_PARAMS, getUrlParam('debug'));
};

/**
 * Prints space-demand debug tables when {@link DEBUG_ENERGY} is `true` or the
 * URL has `?debug=energy` (also `all` / `true`). No-op in Vitest unless
 * `DEBUG_ENERGY` is on.
 *
 * @param result - Space demand result from {@link getSpaceDemandInWhPerM2}
 * @param version - Building version used for window-area context
 * @param heading - Optional banner printed above the tables (e.g. building name)
 */
export const logSpaceDemand = (
  result: ReturnType<typeof getSpaceDemandInWhPerM2>,
  version: Baseline,
  heading?: string,
) => {
  if (!isEnergyDebugEnabled()) {
    return;
  }
  if (heading) {
    console.info(`--- ${heading} --- `);
  }
  const A = Math.max(0, Number(result?.heatedArea) || 0); // Atemp
  const ann = result?.building?.annualWhPerM2 ?? {};
  const Um = result?.building?.Um;

  // Hjälpare (avrundar till hela kWh)
  const kWhm2 = (whm2: number) => Math.round((Number(whm2) || 0) / 1000);
  const kWhTot = (whm2: number) => Math.round(((Number(whm2) || 0) * A) / 1000);
  const row = (post: string, whm2: number) => ({
    Post: post,
    'kWh/m²·år': kWhm2(whm2),
    'kWh/år': kWhTot(whm2),
  });

  // Plocka ut relevanta poster (Wh/m²)
  const transH = ann.transmissionHeat || 0;
  const ventH = ann.ventilationHeat || 0;
  const infilH = ann.airleakageHeat || 0;
  const postHeat = ann.postHeat || 0;
  const preCool = ann.preCool || 0;
  const regLoss = ann.regulation || 0;
  const airing = ann.airing || 0;

  const sol = ann.insolation || 0;
  const occ = ann.occupant || 0;
  const act = ann.activity || 0;
  const tap = ann.tapwater || 0;

  const solUseful = ann.insolationHeat || 0;
  const occUseful = ann.occupantHeat || 0;
  const actUseful = ann.activityHeat || 0;
  const tapUseful = ann.tapwaterHeat || 0;

  const heatDelivered = ann.heat || 0; // inkluderar airing + regulation enl. din modell
  const coolDelivered = ann.cool || 0; // inkluderar pre cooling

  // Summeringar
  const sumLossHeat = transH + ventH + infilH + airing; // värmeläges-förluster inkl. vädring
  const sumGains = sol + occ + act + tap; // interna + sol
  const sumGainsUseful = solUseful + occUseful + actUseful + tapUseful;
  // const calcHeatNeed = Math.max(0, sumLossHeat - sumGainsUseful); // teoretiskt värmebehov före reglering
  const deliveredCoreHeat = Math.max(
    0,
    heatDelivered - airing - regLoss - postHeat,
  ); // levererad värme exkl. airing/reg

  // Fönsterinfo (för snabb sanity)
  const winArea = Number(getWindowArea(version)) || 0;
  const wwrAtemp = A > 0 ? (winArea / A) * 100 : 0;

  const unmetHeat = ann.unmetHeat || 0;
  const unmetCool = ann.unmetCool || 0;

  // const heatDemandServed = deliveredCoreHeat + unmetHeat;
  // const coolDemandServed = coolDelivered + unmetCool;

  // const heatCoverage =
  //   heatDemandServed > 0 ? deliveredCoreHeat / heatDemandServed : 1;
  // const coolCoverage =
  //   coolDemandServed > 0 ? coolDelivered / coolDemandServed : 1;

  console.info('==================== ENERGI DEBUG ====================');
  console.group('Byggnad');
  console.info(`Atemp: ${Math.round(A)} m²`);
  if (typeof Um === 'number')
    console.info(`Um (Htr/A): ${Um.toFixed(3)} W/m²K`);
  console.info(
    `Fönsterarea: ${Math.round(winArea)} m² (${wwrAtemp.toFixed(1)} % av Atemp)`,
  );
  console.info(
    `Värmetimmar: ${result?.building?.heatingHours ?? '–'} | Kyltimmar: ${result?.building?.coolingHours ?? '–'} | LimitTemp: ${typeof result?.building?.limitTemp === 'number' ? result.building.limitTemp.toFixed(1) + ' °C' : '–'}`,
  );
  console.groupEnd();

  console.group('Förluster (värmeläge)');
  console.table([
    row('Transmission', transH),
    row('Ventilation', ventH),
    row('Infiltration', infilH),
    row('Vädring (airing)', airing),
    row('SUMMA förluster', sumLossHeat),
  ]);
  console.groupEnd();

  console.group('Vinster');
  const gainsRows = [
    row('Sol', sol),
    row('Personvärme', occ),
    row('Verksamhet/utrustning', act),
  ];
  if (tap > 0) gainsRows.push(row('Tappvarme-återvinning', tap));
  gainsRows.push(row('SUMMA vinster', sumGains));
  console.table(gainsRows);
  console.groupEnd();

  console.group('Nyttiga vinster');
  const gainsUsefulRows = [
    row('Sol (nyttig)', solUseful),
    row('Personvärme (nyttig)', occUseful),
    row('Verksamhet/utrustning (nyttig)', actUseful),
  ];
  if (tapUseful > 0)
    gainsUsefulRows.push(row('Tappvarme-återvinning (nyttig)', tapUseful));
  gainsUsefulRows.push(row('SUMMA nyttiga vinster', sumGainsUseful));
  console.table(gainsUsefulRows);
  console.groupEnd();

  console.group('System (levererat)');
  console.table([
    row(
      'Värmebehov inkl postH & reg (ej vädring, köksvent, vvc)',
      heatDelivered - airing,
    ),
    row(
      'Värme (levererad, inkl. post heating, reglering & airing)',
      heatDelivered,
    ),
    row('— varav post heating', postHeat),
    row('— varav regleringsförluster', regLoss),
    row('— varav vädring (airing)', airing),
    row(
      'Värme (levererad exkl. post heating, reglering & airing)',
      deliveredCoreHeat,
    ),
    row('Kyla (levererad)', coolDelivered),
    row('— varav pre cooling', preCool),
    row('Otillräcklig värme (unmet)', unmetHeat), // <— NY
    row('Otillräcklig kyla (unmet)', unmetCool), // <— NY
  ]);
  console.groupEnd();

  // const pct = (x: number) => (isFinite(x) ? (100 * x).toFixed(1) + ' %' : '—');
  // console.group('Komfort / täckningsgrad');
  // console.table([
  //   {
  //     Nyckeltal: 'Värme – täckningsgrad (levererad / (lev+unmet))',
  //     Värde: pct(heatCoverage),
  //   },
  //   {
  //     Nyckeltal: 'Kyla – täckningsgrad (levererad / (lev+unmet))',
  //     Värde: pct(coolCoverage),
  //   },
  // ]);
  // console.groupEnd();

  // console.group('Kontroll / balans');
  // console.table([
  //   row('Beräknat värmebehov (förluster − vinster, ≥0)', calcHeatNeed),
  //   row(
  //     'Skillnad: (lev. värme exkl. airing/reg) − beräknat behov',
  //     deliveredCoreHeat - calcHeatNeed,
  //   ),
  // ]);
  // console.groupEnd();

  console.info('======================================================');
};
